<template>
  <v-dialog :value="value" persistent max-width="920" @input="close">
    <v-card>
      <v-card-title class="student-note-title px-6 pt-5 pb-3">
        <div class="d-flex align-center">
          <v-avatar color="deep-orange lighten-5" size="42" class="mr-3">
            <v-icon color="#ff5722">mdi-note-edit-outline</v-icon>
          </v-avatar>
          <div>
            <div class="headline font-weight-bold">
              {{ $t("payment.student_notes") }}
            </div>
            <div class="student-note-subtitle mt-1">
              <v-icon small color="grey darken-1" class="mr-1">
                mdi-account-outline
              </v-icon>
              {{ student_id }}<span v-if="student_name"> · {{ student_name }}</span>
            </div>
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="px-6 pt-5 pb-2">
        <v-alert v-if="load_error" type="error" outlined text dense>
          {{ load_error }}
        </v-alert>

        <template v-if="!readonly">
          <div class="subtitle-1 font-weight-bold mb-2">
            {{ $t("payment.add_student_note") }}
          </div>
          <v-textarea
            v-model="new_note"
            :label="$t('payment.student_note_message')"
            :placeholder="$t('payment.student_note_placeholder')"
            :disabled="loading || saving"
            :rules="note_rules"
            counter="2000"
            maxlength="2000"
            rows="2"
            auto-grow
            outlined
            hide-details="auto"
          />
          <div class="d-flex justify-end mt-3 mb-5">
            <v-btn
              color="#ff5722"
              depressed
              :dark="can_create"
              :disabled="!can_create"
              :loading="creating"
              @click="create_note"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              {{ $t("payment.save_student_note") }}
            </v-btn>
          </div>
        </template>

        <div class="d-flex align-center mb-2">
          <div class="subtitle-1 font-weight-bold">
            {{ $t("payment.student_note_history") }}
          </div>
          <v-spacer />
          <v-chip small color="deep-orange lighten-5" text-color="#ff5722">
            {{ notes.length }}
          </v-chip>
        </div>

        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="#ff5722" />
        </div>

        <v-alert
          v-else-if="!notes.length"
          color="grey lighten-3"
          icon="mdi-note-off-outline"
          text
          class="mb-2"
        >
          {{ $t("payment.no_student_notes") }}
        </v-alert>

        <div v-else class="student-note-table-wrap">
          <v-simple-table class="student-note-table">
            <thead>
              <tr>
                <th class="student-note-date-column">
                  {{ $t("payment.student_note_date") }}
                </th>
                <th>{{ $t("payment.student_note_message") }}</th>
                <th class="student-note-author-column">
                  {{ $t("payment.student_note_author") }}
                </th>
                <th class="student-note-action-column text-center">
                  {{ $t("payment.actions") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in notes" :key="note_key(item)">
                <td class="student-note-date-cell">
                  <v-icon small color="grey darken-1" class="mr-1">
                    mdi-clock-outline
                  </v-icon>
                  {{ format_timestamp(item.timestamp) }}
                </td>
                <td class="student-note-message-cell">
                  <v-textarea
                    v-if="editing_timestamp === item.timestamp"
                    v-model="editing_note"
                    :rules="note_rules"
                    counter="2000"
                    maxlength="2000"
                    rows="2"
                    auto-grow
                    outlined
                    hide-details="auto"
                    :disabled="saving"
                  />
                  <div v-else class="student-note-message">
                    {{ item.note }}
                  </div>
                </td>
                <td>
                  <div class="font-weight-medium">
                    {{ item.officer_name || item.user_id || "-" }}
                  </div>
                  <div
                    v-if="item.officer_name && item.user_id"
                    class="caption grey--text text--darken-1"
                  >
                    {{ item.user_id }}
                  </div>
                </td>
                <td class="text-center text-no-wrap">
                  <template v-if="editing_timestamp === item.timestamp">
                    <v-btn
                      small
                      color="#ff5722"
                      depressed
                      :dark="can_update(item)"
                      :disabled="!can_update(item)"
                      :loading="saving"
                      @click="update_note(item)"
                    >
                      <v-icon small left>mdi-content-save-outline</v-icon>
                      {{ $t("payment.save") }}
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="#ff5722"
                      class="ml-1"
                      :disabled="saving"
                      @click="cancel_edit"
                    >
                      {{ $t("payment.cancel") }}
                    </v-btn>
                  </template>
                  <template v-else-if="item.can_edit && !readonly">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          color="#ff5722"
                          v-bind="attrs"
                          v-on="on"
                          @click="start_edit(item)"
                        >
                          <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("payment.edit") }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          color="error"
                          v-bind="attrs"
                          v-on="on"
                          @click="confirm_delete(item)"
                        >
                          <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("payment.delete") }}</span>
                    </v-tooltip>
                  </template>
                  <span v-else class="grey--text">-</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-4">
        <v-spacer />
        <v-btn outlined color="#ff5722" :disabled="saving" @click="close">
          {{ $t("payment.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="delete_dialog" persistent max-width="560">
      <v-card>
        <v-card-title class="headline">
          <v-icon color="error" class="mr-2">mdi-delete-alert-outline</v-icon>
          {{ $t("payment.confirm_student_note_delete") }}
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            {{ $t("payment.confirm_student_note_delete_message") }}
          </div>
          <v-sheet color="grey lighten-4" rounded class="pa-3 student-note-message">
            {{ deleting_note ? deleting_note.note : "" }}
          </v-sheet>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn
            color="error"
            depressed
            :loading="deleting"
            @click="delete_note"
          >
            <v-icon left>mdi-delete-outline</v-icon>
            {{ $t("payment.delete") }}
          </v-btn>
          <v-btn
            color="#ff5722"
            outlined
            :disabled="deleting"
            @click="close_delete"
          >
            {{ $t("payment.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import payInSlipProvider from "@/services/pay_in_slip.provider";

const payInSlipService = new payInSlipProvider();

export default {
  name: "PaymentStudentNoteDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    student_id: {
      type: String,
      required: true,
    },
    student_name: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    saving: false,
    creating: false,
    deleting: false,
    load_error: null,
    notes: [],
    new_note: "",
    editing_timestamp: null,
    editing_note: "",
    editing_original_note: "",
    delete_dialog: false,
    deleting_note: null,
  }),
  computed: {
    note_rules() {
      return [
        (value) => Boolean(String(value || "").trim()) || this.$t("payment.student_note_required"),
        (value) => String(value || "").length <= 2000 || this.$t("payment.student_note_too_long"),
      ];
    },
    can_create() {
      return (
        !this.readonly &&
        !this.loading &&
        !this.saving &&
        Boolean(String(this.new_note || "").trim()) &&
        String(this.new_note || "").length <= 2000
      );
    },
  },
  watch: {
    value(open) {
      if (open) this.fetch_notes();
      else this.reset_state();
    },
    student_id() {
      if (this.value) this.fetch_notes();
    },
  },
  methods: {
    note_key(item) {
      return `${item.user_id || ""}-${item.timestamp || ""}`;
    },
    normalize_note(value) {
      return String(value || "").trim();
    },
    extract_error(error) {
      const message = error?.response?.data?.message || error?.message;
      if (typeof message === "string") return message;
      return message ? JSON.stringify(message) : this.$t("payment.student_note_load_failed");
    },
    unwrap_response(response) {
      if (response && Array.isArray(response.notes)) return response;
      if (response?.data && Array.isArray(response.data.notes)) return response.data;
      if (response?.payload && Array.isArray(response.payload.notes)) return response.payload;
      return { notes: [] };
    },
    async fetch_notes() {
      if (!/^\d{8}$/.test(String(this.student_id || ""))) return;
      this.loading = true;
      this.load_error = null;
      this.cancel_edit();
      try {
        const response = await payInSlipService.getPaymentStudentNotes(this.student_id);
        this.notes = this.unwrap_response(response).notes;
      } catch (error) {
        this.notes = [];
        this.load_error = this.extract_error(error);
      } finally {
        this.loading = false;
      }
    },
    async create_note() {
      if (this.readonly || !this.can_create) return;
      this.creating = true;
      this.saving = true;
      try {
        await payInSlipService.createPaymentStudentNote({
          student_id: this.student_id,
          note: this.normalize_note(this.new_note),
        });
        this.new_note = "";
        this.$toasted.success(this.$t("payment.student_note_saved"));
        await this.fetch_notes();
      } catch (error) {
        this.$toasted.error(this.extract_error(error), { duration: 0 });
      } finally {
        this.creating = false;
        this.saving = false;
      }
    },
    start_edit(item) {
      if (this.readonly || !item.can_edit) return;
      this.editing_timestamp = item.timestamp;
      this.editing_note = item.note;
      this.editing_original_note = item.note;
    },
    cancel_edit() {
      this.editing_timestamp = null;
      this.editing_note = "";
      this.editing_original_note = "";
    },
    can_update(item) {
      const note = this.normalize_note(this.editing_note);
      return (
        !this.readonly &&
        item.can_edit &&
        !this.saving &&
        Boolean(note) &&
        note.length <= 2000 &&
        note !== this.normalize_note(this.editing_original_note)
      );
    },
    async update_note(item) {
      if (!this.can_update(item)) return;
      this.saving = true;
      try {
        await payInSlipService.updatePaymentStudentNote({
          student_id: this.student_id,
          timestamp: item.timestamp,
          note_user_id: item.user_id,
          note: this.normalize_note(this.editing_note),
        });
        this.$toasted.success(this.$t("payment.student_note_updated"));
        this.cancel_edit();
        await this.fetch_notes();
      } catch (error) {
        this.$toasted.error(this.extract_error(error), { duration: 0 });
      } finally {
        this.saving = false;
      }
    },
    confirm_delete(item) {
      if (this.readonly || !item.can_edit) return;
      this.deleting_note = item;
      this.delete_dialog = true;
    },
    close_delete() {
      if (this.deleting) return;
      this.delete_dialog = false;
      this.deleting_note = null;
    },
    async delete_note() {
      if (this.readonly || !this.deleting_note) return;
      this.deleting = true;
      this.saving = true;
      try {
        await payInSlipService.deletePaymentStudentNote({
          student_id: this.student_id,
          timestamp: this.deleting_note.timestamp,
          note_user_id: this.deleting_note.user_id,
        });
        this.$toasted.success(this.$t("payment.student_note_deleted"));
        this.delete_dialog = false;
        this.deleting_note = null;
        await this.fetch_notes();
      } catch (error) {
        this.$toasted.error(this.extract_error(error), { duration: 0 });
      } finally {
        this.deleting = false;
        this.saving = false;
      }
    },
    format_timestamp(timestamp) {
      if (!timestamp) return "-";
      const date = new Date(String(timestamp).replace(" ", "T"));
      if (Number.isNaN(date.getTime())) return timestamp;
      const locale = this.$i18n.locale === "th" ? "th-TH" : "en-GB";
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(date);
    },
    reset_state() {
      this.loading = false;
      this.saving = false;
      this.creating = false;
      this.deleting = false;
      this.load_error = null;
      this.notes = [];
      this.new_note = "";
      this.cancel_edit();
      this.delete_dialog = false;
      this.deleting_note = null;
    },
    close() {
      if (this.saving) return;
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.student-note-title {
  background: #fffaf7;
}

.student-note-subtitle {
  color: #666;
  font-size: 0.9rem;
  font-weight: 400;
}

.student-note-table-wrap {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 390px;
  overflow: auto;
}

.student-note-table thead th {
  background: #f5f5f5;
  color: #444 !important;
  font-size: 0.9rem;
  position: sticky;
  top: 0;
  z-index: 1;
}

.student-note-table tbody tr:nth-child(even) {
  background: #fffaf5;
}

.student-note-table td {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  vertical-align: top;
}

.student-note-date-column {
  min-width: 190px;
  width: 190px;
}

.student-note-author-column {
  min-width: 150px;
  width: 150px;
}

.student-note-action-column {
  min-width: 150px;
  width: 150px;
}

.student-note-date-cell {
  color: #555;
  white-space: nowrap;
}

.student-note-message-cell {
  min-width: 280px;
}

.student-note-message {
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
</style>
